export interface FormData {
  firstName: string;
  lastName: string;
  email: string;
  company: string;
  service: string;
  message: string;
  recaptchaToken: string;
}

export class SecureApiService {
  private baseUrl: string;

  constructor() {
    const url = process.env.REACT_APP_AZURE_FUNCTIONS_URL;
    if (!url) throw new Error("REACT_APP_AZURE_FUNCTIONS_URL is not set");
    this.baseUrl = url;
  }

  private async secureApiCall<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<{
    success: boolean;
    data?: T;
    message?: string;
    errors?: string[];
  }> {
    try {
      const response = await fetch(`${this.baseUrl}${endpoint}`, {
        ...options,
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          ...options.headers,
        },
        credentials: "include",
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error("API call failed:", error);
      throw error instanceof Error
        ? error
        : new Error("An unexpected error occurred");
    }
  }

  async submitContactForm(
    formData: FormData
  ): Promise<{ success: boolean; message?: string; errors?: string[] }> {
    const validationErrors = this.validateContactForm(formData);
    if (validationErrors.length > 0) {
      return { success: false, errors: validationErrors };
    }
    const token = await window.grecaptcha.enterprise.execute('6LefLYIrAAAAAIvPFoBEfio_TSACJ_hN3XJ88T3g', { action: 'submit' });
    const sanitizedData = this.sanitizeContactForm(formData);

    return this.secureApiCall("/contact/submit", {
      method: "POST",
      body: JSON.stringify({ ...sanitizedData, recaptchaToken: token }),
    });
  }

  async subscribeNewsletter(
    email: string
  ): Promise<{ success: boolean; message?: string; errors?: string[] }> {
    if (!this.isValidEmail(email)) {
      return {
        success: false,
        errors: ["Please provide a valid email address"],
      };
    }

    return this.secureApiCall("/newsletter/subscribe", {
      method: "POST",
      body: JSON.stringify({ email: email.toLowerCase().trim() }),
    });
  }

  private validateContactForm(data: FormData): string[] {
    const errors: string[] = [];
    if (!data.firstName?.trim()) errors.push("First name is required");
    if (!data.lastName?.trim()) errors.push("Last name is required");
    if (!this.isValidEmail(data.email)) errors.push("Valid email is required");
    if (!data.message?.trim() || data.message.length < 10) {
      errors.push("Message must be at least 10 characters");
    }
    return errors;
  }

  private sanitizeContactForm(data: FormData): FormData {
    return {
      firstName: this.sanitizeString(data.firstName),
      lastName: this.sanitizeString(data.lastName),
      email: data.email.toLowerCase().trim(),
      company: this.sanitizeString(data.company),
      service: data.service,
      message: this.sanitizeString(data.message),
      recaptchaToken: data.recaptchaToken.trim(),
    };
  }

  private sanitizeString(str: string): string {
    return str.trim().replace(/[<>]/g, "").substring(0, 1000);
  }

  private isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }
}